import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Stack,
    ButtonGroup,
    Chip,
} from "@mui/joy";
import {
    ChevronLeft,
    ChevronRight,
    HelpCircle,
    Settings,
    User,
    List,
    Clock,
    Calendar as CalendarIcon,
} from "lucide-react";
import { Button, IconButton } from "../../shared";
import { NewEvent } from "./NewEvent";
import { useTask } from "../../hooks/query";
import {
    getDaysInMonth,
    formatDate,
    isCurrentMonth,
    isToday,
    isSelected,
    getEventColor,
    getEventTextColor,
    getStatusColor,
    getStatusName,
} from "./utils";

import { useColorScheme } from "@mui/joy/styles";

interface Event {
    id: string;
    title: string;
    date: string;
    time?: string;
    allDay: boolean;
    color: "purple" | "indigo" | "violet" | "red" | "green" | "blue" | "orange" | "yellow";
    description?: string;
    projectId?: string;
    taskStatus?: string;
    tags?: Record<string, string>;
}

interface CalendarV2Props {}

export default function CalendarV2(props: CalendarV2Props = {}) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showModal, setShowModal] = useState(false);
    const [showEventDetails, setShowEventDetails] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
    const [currentView, setCurrentView] = useState<"day" | "week" | "month">("month");
    const [events, setEvents] = useState<Event[]>([]);
    const { mode } = useColorScheme();

    // Task API hooks
    const { useListSubtasks } = useTask();
    const listSubtasksSvc = useListSubtasks();

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Fetch subtasks and convert to events
    useEffect(() => {
        const fetchSubtasks = async () => {
            try {
                const response = await listSubtasksSvc.mutateAsync({});
                const subtasks = response || [];

                // Convert subtasks to events
                const convertedEvents: Event[] = subtasks.map((subtask: any) => {
                    // Extract date from ExecutionDate
                    const executionDate = subtask.ExecutionDate
                        ? new Date(subtask.ExecutionDate).toISOString().split("T")[0]
                        : formatDate(new Date());

                    // Extract time from ExecutionDate
                    const executionTime = subtask.ExecutionDate
                        ? new Date(subtask.ExecutionDate).toTimeString().slice(0, 5)
                        : undefined;

                    // Get color from tags, default to purple
                    const colorFromTags = subtask.Tags?.Color?.toLowerCase() || "purple";
                    const validColor = [
                        "purple",
                        "indigo",
                        "violet",
                        "red",
                        "green",
                        "blue",
                        "orange",
                        "yellow",
                    ].includes(colorFromTags)
                        ? (colorFromTags as Event["color"])
                        : "purple";

                    return {
                        id: subtask.Id,
                        title: subtask.Name,
                        date: executionDate,
                        time: executionTime,
                        allDay: false,
                        color: validColor,
                        description: subtask.Description,
                        projectId: subtask.ProjectId,
                        taskStatus: subtask.Status,
                        tags: subtask.Tags || {},
                    };
                });

                setEvents(convertedEvents);
            } catch (error) {
                console.error("Error fetching subtasks:", error);
            }
        };

        fetchSubtasks();
    }, []);

    const getEventsForDate = (date: string) => {
        return events.filter((event) => event.date === date);
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        setShowModal(true);
    };

    const handleEventClick = (event: Event, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedEvent(event);
        setShowEventDetails(true);
    };

    const handleAddEvent = (newEventData: any) => {
        // The NewSubTaskInline component handles database creation
        // We just need to refresh the events from the API
        const refreshEvents = async () => {
            try {
                const response = await listSubtasksSvc.mutateAsync({});
                const subtasks = response || [];

                // Convert subtasks to events
                const convertedEvents: Event[] = subtasks.map((subtask: any) => {
                    // Extract date from ExecutionDate
                    const executionDate = subtask.ExecutionDate
                        ? new Date(subtask.ExecutionDate).toISOString().split("T")[0]
                        : formatDate(new Date());

                    // Extract time from ExecutionDate
                    const executionTime = subtask.ExecutionDate
                        ? new Date(subtask.ExecutionDate).toTimeString().slice(0, 5)
                        : undefined;

                    // Get color from tags, default to purple
                    const colorFromTags = subtask.Tags?.Color?.toLowerCase() || "purple";
                    const validColor = [
                        "purple",
                        "indigo",
                        "violet",
                        "red",
                        "green",
                        "blue",
                        "orange",
                        "yellow",
                    ].includes(colorFromTags)
                        ? (colorFromTags as Event["color"])
                        : "purple";

                    return {
                        id: subtask.Id,
                        title: subtask.Name,
                        date: executionDate,
                        time: executionTime,
                        allDay: false,
                        color: validColor,
                        description: subtask.Description,
                        projectId: subtask.ProjectId,
                        taskStatus: subtask.Status,
                        tags: subtask.Tags || {},
                    };
                });

                setEvents(convertedEvents);
            } catch (error) {
                console.error("Error refreshing events:", error);
            }
        };

        refreshEvents();
        setShowModal(false);
    };

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const calendarDays = getDaysInMonth(currentDate);

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "background.surface", flex: 1 }}>
            {/* Enhanced Calendar Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                    flexShrink: 0,
                }}
            >
                {/* Left side - Month/Year and Navigation */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box display="flex" gap={1} minWidth={200}>
                        <Typography level="h3" sx={{ fontWeight: 600, color: "text.primary" }}>
                            {currentDate.toLocaleDateString("en-US", { month: "long" })}
                        </Typography>
                        <Typography level="h3" sx={{ fontWeight: 400, color: "text.primary" }}>
                            {currentDate.toLocaleDateString("en-US", { year: "numeric" })}
                        </Typography>
                    </Box>

                    <Button variant="outlined" size="sm" onClick={handleToday}>
                        Today
                    </Button>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton variant="outlined" size="sm" onClick={handlePrevMonth}>
                            <ChevronLeft size={20} />
                        </IconButton>
                        <IconButton variant="outlined" size="sm" onClick={handleNextMonth}>
                            <ChevronRight size={20} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Right side - Icons and View Toggles */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton size="sm" variant="plain">
                        <Settings size={16} />
                    </IconButton>
                </Box>
            </Box>

            {/* Days of Week Header */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                    flexShrink: 0,
                }}
            >
                {daysOfWeek.map((day) => (
                    <Typography
                        key={day}
                        level="title-md"
                        sx={{
                            ml: 2,
                            my: 1,
                            borderRight: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        {day}
                    </Typography>
                ))}
            </Box>

            {/* Calendar Grid */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    flex: 1,
                    minHeight: 0,
                    bgcolor: "background.surface",
                }}
            >
                {calendarDays.map((date, index) => {
                    const dateStr = formatDate(date);
                    const dayEvents = getEventsForDate(dateStr);
                    const isCurrentMonth =
                        date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();

                    return (
                        <Box
                            key={index}
                            sx={{
                                borderRight: "1px solid",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                minHeight: 120,
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                opacity: 1,
                                "&:hover": {
                                    bgcolor: mode === "dark" ? "primary.900" : "primary.50",
                                    "& .hover-add-button": {
                                        opacity: 1,
                                    },
                                },
                                ...(isToday(date) && {
                                    bgcolor: mode === "dark" ? "primary.800" : "primary.50",
                                    color: "white",
                                }),
                                ...(isSelected(date, selectedDate) && {
                                    bgcolor: mode === "dark" ? "primary.900" : "primary.50",
                                    color: mode === "dark" ? "primary.50" : "primary.600",
                                }),
                                "&:nth-of-type(7n)": {
                                    borderRight: "none",
                                },
                            }}
                            onClick={() => handleDateClick(date)}
                        >
                            {/* Hover + Button - Only show for current month */}
                            {isCurrentMonth && (
                                <Box
                                    className="hover-add-button"
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        opacity: 0,
                                        transition: "opacity 0.2s ease",
                                        zIndex: 1,
                                    }}
                                >
                                    <IconButton
                                        size="sm"
                                        variant="solid"
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: { xs: "primary.500", dark: "primary.400" },
                                            color: "white",
                                            "&:hover": {
                                                bgcolor: { xs: "primary.600", dark: "primary.500" },
                                            },
                                        }}
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleDateClick(date);
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                lineHeight: 1,
                                            }}
                                        >
                                            +
                                        </Box>
                                    </IconButton>
                                </Box>
                            )}

                            <Box sx={{ p: 1.5, flex: 1, display: "flex", flexDirection: "column" }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 600,
                                        fontSize: "sm",
                                        opacity: isCurrentMonth ? 1 : 0.3,
                                        mb: 1,
                                        flexShrink: 0,
                                        ...(isToday(date) && {
                                            bgcolor: { xs: "primary.600", dark: "primary.500" },
                                            color: { xs: "white", dark: "white" },
                                        }),
                                    }}
                                >
                                    {date.getDate()}
                                </Box>
                                <Stack spacing={0.5} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                                    {dayEvents.slice(0, 2).map((event) => (
                                        <Box
                                            key={event.id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                bgcolor: getEventColor(event.color, mode),
                                                color: getEventTextColor(event.color, mode),
                                                px: 1,
                                                pl: 1,
                                                py: 0.5,
                                                ml: -1,
                                                mr: -1,
                                                fontSize: "xs",
                                                fontWeight: 500,
                                                borderRadius: "5px",
                                                cursor: "pointer",
                                                "&:hover": { opacity: 0.8 },
                                                position: "relative",
                                                overflow: "hidden",
                                            }}
                                            onClick={(e) => handleEventClick(event, e)}
                                        >
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 6,
                                                    bottom: 6,
                                                    width: 2,
                                                    bgcolor: getEventTextColor(event.color, mode),
                                                }}
                                            />
                                            <Typography
                                                level="body-xs"
                                                sx={{
                                                    color: getEventTextColor(event.color, mode),
                                                    fontWeight: 500,
                                                    ml: 0,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {event.title}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {dayEvents.length > 2 && (
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                color: "text.secondary",
                                                textAlign: "center",
                                                p: 0.5,
                                                bgcolor: "background.level1",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                "&:hover": { bgcolor: "background.level2" },
                                            }}
                                        >
                                            +{dayEvents.length - 2} more
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* New Event Modal */}
            <NewEvent
                showModal={showModal}
                setShowModal={setShowModal}
                selectedDate={selectedDate}
                onSubmit={handleAddEvent}
            />

            {/* Event Details Modal */}
            <Modal open={showEventDetails} onClose={() => setShowEventDetails(false)}>
                <ModalDialog size="md">
                    <ModalClose />
                    <DialogTitle>Event Details</DialogTitle>
                    <DialogContent>
                        {selectedEvent && (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography level="body-sm" sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>
                                        {selectedEvent.title}
                                    </Typography>
                                    <Chip
                                        size="sm"
                                        sx={{
                                            bgcolor: getEventColor(selectedEvent.color, mode),
                                            color: getEventTextColor(selectedEvent.color, mode),
                                            fontWeight: 500,
                                        }}
                                    >
                                        {selectedEvent.allDay ? "All Day" : selectedEvent.time}
                                    </Chip>
                                </Box>

                                {selectedEvent.description && (
                                    <Box>
                                        <Typography
                                            level="body-sm"
                                            sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                                        >
                                            Description
                                        </Typography>
                                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                                            {selectedEvent.description}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedEvent.taskStatus && (
                                    <Box>
                                        <Typography
                                            level="body-sm"
                                            sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                                        >
                                            Status
                                        </Typography>
                                        <Chip
                                            size="sm"
                                            sx={{
                                                bgcolor: getStatusColor(selectedEvent.taskStatus, mode),
                                                color: "white",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {getStatusName(selectedEvent.taskStatus)}
                                        </Chip>
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CalendarIcon size={16} />
                                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                                        {new Date(selectedEvent.date).toLocaleDateString("en-US", {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })}
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setShowEventDetails(false)}>
                            Close
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
